const CUT_FEE_SKU = 'CUT_FEE';
const CUT_FEE_LABEL = 'Custom Cutting & Handling';
const CUT_FEE_AMOUNT_CENTS = 3000;
const ERROR_MESSAGE = 'We could not update the cart totals. Please try again before checking out.';
const SOURCE = 'gg-cut-fee';
const CART_UPDATE_EVENT = 'cart:update';
const CART_ERROR_EVENT = 'cart:error';

let isSyncingCutFee = false;
let syncQueued = false;
let activeSyncPromise = null;

function getRoute(name, fallback) {
  return window.Theme?.routes?.[name] || fallback;
}

function getCutFeeVariantId() {
  const value = window.Theme?.cutFee?.variantId;
  return value ? Number(value) : 0;
}

function isCutFeeLine(item) {
  return item && item.sku === CUT_FEE_SKU;
}

function isCutRollLine(item) {
  return (
    item &&
    !isCutFeeLine(item) &&
    item.properties?._turf_cut === 'true' &&
    item.properties?._cut_type === 'partial'
  );
}

function getRequiredCutFeeQuantity(cart) {
  return (cart?.items || []).reduce((total, item) => {
    if (!isCutRollLine(item)) return total;
    const quantity = Number(item.quantity);
    return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
  }, 0);
}

function getVisibleCartItemCount(cart) {
  return (cart?.items || []).reduce((total, item) => {
    if (isCutFeeLine(item)) return total;
    return total + Number(item.quantity || 0);
  }, 0);
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch(`${getRoute('cart_url', '/cart')}.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error('Unable to fetch cart');
  return response.json();
}

async function changeCartLineByKey(lineKey, quantity) {
  const sectionIds = getCartSectionIds();
  const response = await fetch(getRoute('cart_change_url', '/cart/change.js'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: lineKey,
      quantity,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) throw new Error('Unable to change cart line');
  return response.json();
}

async function addCutFeeVariant(variantId, quantity) {
  const sectionIds = getCartSectionIds();
  const response = await fetch(getRoute('cart_add_url', '/cart/add.js'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity,
          properties: {
            _internal_fee: 'cut_fee',
            _fee_label: CUT_FEE_LABEL,
          },
        },
      ],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) throw new Error('Unable to add cut fee');
  return response.json();
}

function dispatchCartUpdate(cart, sections) {
  document.dispatchEvent(
    new CustomEvent(CART_UPDATE_EVENT, {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: SOURCE,
        data: {
          source: SOURCE,
          itemCount: getVisibleCartItemCount(cart),
          sections,
        },
      },
    })
  );
}

function dispatchCutFeeError(error) {
  console.error('[Cut fee] Failed to sync cart fee', error);
  document.dispatchEvent(
    new CustomEvent(CART_ERROR_EVENT, {
      bubbles: true,
      detail: {
        sourceId: SOURCE,
        data: {
          message: ERROR_MESSAGE,
          description: ERROR_MESSAGE,
          errors: error,
        },
      },
    })
  );
}

function syncCutFee(options = {}) {
  if (isSyncingCutFee) {
    syncQueued = true;
    return activeSyncPromise;
  }

  activeSyncPromise = performCutFeeSync(options).finally(() => {
    activeSyncPromise = null;
  });

  return activeSyncPromise;
}

async function performCutFeeSync({ dispatchUpdate = true } = {}) {
  isSyncingCutFee = true;

  try {
    const variantId = getCutFeeVariantId();
    const cart = await fetchCart();
    const requiredQuantity = getRequiredCutFeeQuantity(cart);
    const feeLines = cart.items.filter(isCutFeeLine);
    let latestResponse = null;
    let changed = false;

    if (requiredQuantity === 0) {
      for (const line of feeLines) {
        latestResponse = await changeCartLineByKey(line.key, 0);
        changed = true;
      }
    } else {
      if (!variantId) {
        throw new Error('CUT_FEE variant ID is not configured in theme settings.');
      }

      if (feeLines.length === 0) {
        latestResponse = await rebuildCartWithCutFeeFirst(cart, variantId, requiredQuantity);
        changed = true;
      } else {
        const [primaryFeeLine, ...duplicateFeeLines] = feeLines;
        const [firstCartLine] = cart.items;
        const shouldMoveFeeLineToStart = !isCutFeeLine(firstCartLine);

        if (shouldMoveFeeLineToStart) {
          latestResponse = await rebuildCartWithCutFeeFirst(cart, variantId, requiredQuantity);
          changed = true;
        } else {
          for (const duplicate of duplicateFeeLines) {
            latestResponse = await changeCartLineByKey(duplicate.key, 0);
            changed = true;
          }

          if (primaryFeeLine.quantity !== requiredQuantity) {
            latestResponse = await changeCartLineByKey(primaryFeeLine.key, requiredQuantity);
            changed = true;
          }
        }
      }
    }

    const updatedCart = changed ? await fetchCart() : cart;

    if (changed && dispatchUpdate) {
      dispatchCartUpdate(updatedCart, latestResponse?.sections);
    }

    return updatedCart;
  } finally {
    isSyncingCutFee = false;

    if (syncQueued) {
      syncQueued = false;
      queueMicrotask(() => syncCutFee().catch(dispatchCutFeeError));
    }
  }
}

async function clearCart() {
  const response = await fetch(`${getRoute('cart_url', '/cart')}/clear.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) throw new Error('Unable to clear cart');
  return response.json();
}

async function addCartItems(items) {
  const sectionIds = getCartSectionIds();
  const response = await fetch(getRoute('cart_add_url', '/cart/add.js'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) throw new Error('Unable to rebuild cart');
  return response.json();
}

async function rebuildCartWithCutFeeFirst(cart, variantId, requiredQuantity) {
  const items = [
    {
      id: variantId,
      quantity: requiredQuantity,
      properties: {
        _internal_fee: 'cut_fee',
        _fee_label: CUT_FEE_LABEL,
      },
    },
    ...cart.items
      .filter((item) => !isCutFeeLine(item))
      .map((item) => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
        selling_plan: item.selling_plan_allocation?.selling_plan?.id,
      })),
  ];

  await clearCart();
  return addCartItems(items);
}

function shouldIgnoreCartUpdate(event) {
  return event.detail?.data?.source === SOURCE || event.detail?.sourceId === SOURCE;
}

function handleCartUpdate(event) {
  if (shouldIgnoreCartUpdate(event)) return;
  syncCutFee().catch(dispatchCutFeeError);
}

function goToCheckoutAfterSync(event) {
  event.preventDefault();
  event.stopPropagation();

  syncCutFee({ dispatchUpdate: true })
    .then(() => {
      window.location.href = `${getRoute('cart_url', '/cart').replace(/\/cart$/, '')}/checkout`;
    })
    .catch(dispatchCutFeeError);
}

function handleCheckout(event) {
  const target = event.target instanceof Element ? event.target : null;
  const checkoutButton = target?.closest('#checkout, [name="checkout"]');
  if (!checkoutButton) return;

  goToCheckoutAfterSync(event);
}

function handleCartSubmit(event) {
  const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
  if (submitter?.matches('#checkout, [name="checkout"]')) {
    goToCheckoutAfterSync(event);
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.GGCutFee = {
    CUT_FEE_SKU,
    CUT_FEE_LABEL,
    CUT_FEE_AMOUNT_CENTS,
    getVisibleCartItemCount,
    syncCutFee,
  };

  document.addEventListener(CART_UPDATE_EVENT, handleCartUpdate);
  document.addEventListener('click', handleCheckout, true);
  document.addEventListener('submit', handleCartSubmit, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncCutFee().catch(dispatchCutFeeError), { once: true });
  } else {
    syncCutFee().catch(dispatchCutFeeError);
  }
}

export { getRequiredCutFeeQuantity, isCutFeeLine, isCutRollLine };
